import { apiClient } from './client'
import type { FeatureDefinition } from '@/types/feature-definition'

export interface FeatureAssignment {
  id: string
  hospitalId: string
  featureId: string
  enabled: boolean
  value: unknown
  createdAt: string
  updatedAt: string
  createdBy: string | null
  updatedBy: string | null
  feature?: FeatureDefinition
}

export interface AssignFeatureRequest {
  featureId: string
  enabled: boolean
  value?: unknown
}

export interface BulkAssignRequest {
  assignments: AssignFeatureRequest[]
}

export interface AvailableFeaturesResponse {
  success: boolean
  data: FeatureDefinition[]
}

export interface AssignedFeaturesResponse {
  success: boolean
  data: FeatureAssignment[]
}

export interface FeatureAssignmentResponse {
  success: boolean
  data: FeatureAssignment
}

export interface BulkAssignResponse {
  success: boolean
  data: {
    assigned: number
    assignments: FeatureAssignment[]
  }
}

export interface PreviewFeatureRequest {
  featureId: string
  enabled: boolean
  value: unknown
}

export interface PreviewFeatureResponse {
  success: boolean
  data: {
    featureKey: string
    featureName: string
    currentState: {
      exists: boolean
      value: unknown
      enabled: boolean | null
    }
    proposedState: {
      value: unknown
      enabled: boolean
    }
    storagePath: string
  }
}

export const featureAssignmentsApi = {
  /**
   * Get available features for a hospital (based on availability rules)
   */
  getAvailableFeatures: async (hospitalId: string): Promise<AvailableFeaturesResponse> => {
    const response = await apiClient.get<AvailableFeaturesResponse>(
      `/hospitals/${hospitalId}/feature-assignments/available`
    )
    return response.data
  },

  /**
   * Get currently assigned features for a hospital
   */
  getAssignedFeatures: async (hospitalId: string): Promise<AssignedFeaturesResponse> => {
    const response = await apiClient.get<AssignedFeaturesResponse>(
      `/hospitals/${hospitalId}/feature-assignments/assigned`
    )
    return response.data
  },

  /**
   * Assign a feature to a hospital
   */
  assignFeature: async (
    hospitalId: string,
    data: AssignFeatureRequest
  ): Promise<FeatureAssignmentResponse> => {
    const response = await apiClient.post<FeatureAssignmentResponse>(
      `/hospitals/${hospitalId}/feature-assignments/assign`,
      data
    )
    return response.data
  },

  /**
   * Unassign a feature from a hospital
   */
  unassignFeature: async (
    hospitalId: string,
    featureId: string
  ): Promise<{ success: boolean; data: { message: string } }> => {
    const response = await apiClient.delete<{ success: boolean; data: { message: string } }>(
      `/hospitals/${hospitalId}/feature-assignments/${featureId}`
    )
    return response.data
  },

  /**
   * Bulk assign multiple features to a hospital
   */
  bulkAssign: async (hospitalId: string, data: BulkAssignRequest): Promise<BulkAssignResponse> => {
    const response = await apiClient.post<BulkAssignResponse>(
      `/hospitals/${hospitalId}/feature-assignments/bulk-assign`,
      data
    )
    return response.data
  },

  /**
   * Preview feature assignment changes without modifying the database
   */
  previewFeatureAssignment: async (
    hospitalId: string,
    data: PreviewFeatureRequest
  ): Promise<PreviewFeatureResponse> => {
    const response = await apiClient.post<PreviewFeatureResponse>(
      `/hospitals/${hospitalId}/feature-assignments/preview`,
      data
    )
    return response.data
  },

  /**
   * Update an existing feature assignment
   */
  updateFeatureAssignment: async (
    hospitalId: string,
    featureId: string,
    data: { value?: unknown; enabled?: boolean }
  ): Promise<FeatureAssignmentResponse> => {
    const response = await apiClient.patch<FeatureAssignmentResponse>(
      `/hospitals/${hospitalId}/feature-assignments/${featureId}`,
      data
    )
    return response.data
  },
}
